// Lightweight SEO-meta hook — sets document.title, meta description, canonical
// link, Open Graph + Twitter card tags, and (optionally) JSON-LD article
// schema. We avoid pulling in a full helmet library; this hook covers the
// 95% case for blog pages and is ~50 lines.
//
// IMPORTANT: this updates the live DOM after JS executes. Google's crawler
// runs JS and will pick these up, but social-preview crawlers (Slack, FB,
// Twitter) generally do NOT. For social previews to render correctly the
// blog detail pages would need server-side rendering — out of scope for now.

import { useEffect } from 'react';

export interface SeoMeta {
  title: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  // If set, also injects an Article JSON-LD payload.
  jsonLd?: Record<string, unknown>;
}

const MANAGED_NAMES = [
  'description',
  'og:title',
  'og:description',
  'og:image',
  'og:url',
  'og:type',
  'twitter:card',
  'twitter:title',
  'twitter:description',
  'twitter:image',
];

function setMeta(attr: 'name' | 'property', key: string, value: string | undefined) {
  if (!value) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.dataset.seoManaged = '1';
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setLink(rel: string, href: string | undefined) {
  if (!href) return;
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.dataset.seoManaged = '1';
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(payload: Record<string, unknown> | undefined) {
  // Remove any prior managed JSON-LD blob.
  document.head
    .querySelectorAll('script[type="application/ld+json"][data-seo-managed="1"]')
    .forEach((n) => n.remove());
  if (!payload) return;
  const s = document.createElement('script');
  s.setAttribute('type', 'application/ld+json');
  s.dataset.seoManaged = '1';
  s.textContent = JSON.stringify(payload);
  document.head.appendChild(s);
}

export function useSeo(meta: SeoMeta) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = meta.title;
    setMeta('name', 'description', meta.description);
    setMeta('property', 'og:title', meta.title);
    setMeta('property', 'og:description', meta.description);
    setMeta('property', 'og:type', meta.ogType ?? 'website');
    setMeta('property', 'og:image', meta.ogImage);
    setMeta('property', 'og:url', meta.canonical);
    setMeta('name', 'twitter:card', meta.ogImage ? 'summary_large_image' : 'summary');
    setMeta('name', 'twitter:title', meta.title);
    setMeta('name', 'twitter:description', meta.description);
    setMeta('name', 'twitter:image', meta.ogImage);
    setLink('canonical', meta.canonical);
    setJsonLd(meta.jsonLd);

    return () => {
      document.title = prevTitle;
      // We deliberately don't strip managed meta here — keeping the last set
      // values around is harmless and avoids a brief "meta-less" flash on
      // route change. Next page's useSeo will overwrite them.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    meta.title,
    meta.description,
    meta.canonical,
    meta.ogImage,
    meta.ogType,
    JSON.stringify(meta.jsonLd ?? null),
  ]);
}

void MANAGED_NAMES;
